import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as authSession from './authSession';
import { logoutUser } from '../api/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore (or reject) the session on mount — this is what makes a page
  // reload preserve a still-valid 48h session and immediately drop an
  // already-expired one, all synchronously from localStorage, with no
  // network round trip needed before PrivateRoute can decide.
  useEffect(() => {
    const session = authSession.loadSession();

    if (!session || authSession.isSessionExpired(session)) {
      authSession.clearSession();
      setUser(null);
    } else {
      setUser(session.user);
      authSession.scheduleAbsoluteLogout(session.sessionExpiresAt, authSession.forceLogout);
    }

    setLoading(false);

    const unsubscribeCrossTab = authSession.onCrossTabLogout(() => {
      // Another tab already cleared storage and is navigating itself away;
      // this tab just needs to stop showing protected content too.
      setUser(null);
    });

    const unsubscribeRefreshed = authSession.onRefreshed((event) => {
      // The axios interceptor refreshed the access token (and possibly
      // updated role/library) without this component's involvement — pick
      // up the new user object so role/library-gated UI reacts to it.
      setUser(event.detail.user);
    });

    const unsubscribeLogout = authSession.onLogoutEvent(() => {
      setUser(null);
    });

    return () => {
      unsubscribeCrossTab();
      unsubscribeRefreshed();
      unsubscribeLogout();
      authSession.clearLogoutTimer();
    };
  }, []);

  const login = useCallback((loggedInUser, accessToken, sessionExpiresAt) => {
    authSession.saveSession({ user: loggedInUser, accessToken, sessionExpiresAt });
    setUser(loggedInUser);
    authSession.scheduleAbsoluteLogout(sessionExpiresAt, authSession.forceLogout);
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutUser();
    } catch {
      // Best-effort server-side revocation — local logout must proceed
      // regardless of a network hiccup here.
    }
    authSession.performLogout({ expired: false });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        isAuthenticated: !!user,
        roleId: user?.role_id,
        libraryId: user?.library_id,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
